'use client';

import { RegisterForm } from '@/app/(auth)/components/RegisterForm';
import VideoCarousel from '@/shared/components/VideoCarousel';
import Link from 'next/link';
import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { organizationInviteStorage } from '@/features/organization';

export default function RegisterPage() {
	const searchParams = useSearchParams();

	// Store organization invite data when redirected from callback
	useEffect(() => {
		const inviteData =
			organizationInviteStorage.extractFromSearchParams(searchParams);
		if (inviteData) {
			organizationInviteStorage.save(inviteData);
		}
	}, [searchParams]);

	return (
		<div className="min-h-screen relative overflow-x-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Dark overlay for contrast */}
			<div className="fixed inset-0 bg-black/50 z-[1] pointer-events-none"></div>

			{/* Close button to return home */}
			<Link
				href="/"
				className="absolute top-6 left-6 z-50 w-10 h-10 flex items-center justify-center glass-light glass-hover rounded-full text-white shadow-xl"
				aria-label="Go back to homepage"
			>
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			</Link>

			{/* Centered Glass Form Container */}
			<div className="relative z-10 min-h-screen flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
				<div className="w-full max-w-md space-y-8 py-20 lg:py-0">
					{/* Header */}
					<div className="text-center space-y-2">
						<h1 className="text-4xl font-light text-white drop-shadow-lg">
							Create without limits
						</h1>
						<p className="text-white/60 text-sm drop-shadow-md">
							From concept to final cut — bring your vision to
							life.
						</p>
					</div>

					{/* Register Form in Glass Card */}
					<div className="relative group">
						{/* Outer glow effect */}
						<div className="absolute -inset-0.5 bg-gradient-to-r from-white/10 via-white/5 to-white/10 rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-500"></div>

						{/* Glass Card */}
						<div className="relative p-8 lg:p-10 rounded-3xl overflow-hidden bg-white/5 border border-white/30 shadow-2xl">
							{/* Specular highlight */}
							<div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-20 pointer-events-none"></div>

							{/* Content */}
							<div className="relative z-10">
								<RegisterForm />
							</div>
						</div>
					</div>

					{/* Terms and Privacy */}
					<div className="text-center text-xs text-white/70 drop-shadow-lg">
						<p>
							By creating an account, you agree to our{' '}
							<Link
								href="/terms"
								className="text-red-400 hover:underline font-medium"
							>
								Terms of Service
							</Link>{' '}
							and acknowledge that you have read and understand
							our{' '}
							<Link
								href="/privacy"
								className="text-red-400 hover:underline font-medium"
							>
								Privacy Policy
							</Link>
							.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
